/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from '@emotion/styled'
import moment from 'moment'
import { Button, Modal } from 'react-bootstrap'

import { useLabPermission } from 'hooks/useLabPermission'
import { UserPermission } from 'components/lab/user/permissions'

import Toast from 'components/atoms/Toast'
import DataTable from 'components/atoms/DataTable'
import Input from 'components/atoms/Input'
import { TableWrapper } from 'components/atoms/TableWrapper'
import { confirm } from 'components/molecules/Confirmation'
import {
  useCreateTagMutation,
  useDeleteTagMutation,
  useGetTagsQuery,
  useUpdateTagMutation,
} from 'store/services/tags'
import {
  TagDto
} from 'store/types'
import DashboardContent from '../atoms/DashboardContent'
import routes from '../constants/routes'

const StyledTableWrapper = styled(TableWrapper)`
  max-height: calc(100vh - 230px);
  th {
    padding: 0 0.2rem 0.4rem 0.1rem;
  }
  td {
    vertical-align: middle;
    padding: 0.6rem;
  }
`

const PAGE_SIZE = 10

const NAMESPACE_OPTIONS = [
  { label: 'Patient', value: 'PATIENT' },
]

type TagFormState = {
  text: string
  namespace: 'PATIENT'
}

const EMPTY_FORM: TagFormState = {
  text: '',
  namespace: 'PATIENT',
}

type MutationError = {
  data?: {
    message?: string
  }
}

const TagDirectory = () => {
  const navigate = useNavigate()
  const canManageTags = useLabPermission(UserPermission.MANAGE_TAGS)
  const [search, setSearch] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingTag, setEditingTag] = useState<TagDto | null>(null)
  const [form, setForm] = useState<TagFormState>(EMPTY_FORM)

  useEffect(() => {
    if (!canManageTags) {
      navigate(routes.index)
    }
  }, [canManageTags, navigate])

  const { data: tags, isLoading } = useGetTagsQuery()
  const [createTag, { isLoading: createLoading }] = useCreateTagMutation()
  const [updateTag, { isLoading: updateLoading }] = useUpdateTagMutation()
  const [deleteTag] = useDeleteTagMutation()

  const filteredTags = useMemo(
    () =>
      tags?.filter(
        (tag) =>
          !search
          || tag.text.toLowerCase().includes(search.toLowerCase())
          || tag.namespace.toLowerCase().includes(search.toLowerCase()),
      ) || [],
    [search, tags],
  )

  const openCreateModal = () => {
    setEditingTag(null)
    setForm(EMPTY_FORM)
    setShowModal(true)
  }

  const openEditModal = (tag: TagDto) => {
    setEditingTag(tag)
    setForm({
      text: tag.text,
      namespace: tag.namespace,
    })
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingTag(null)
    setForm(EMPTY_FORM)
  }

  const getErrorMessage = (error: unknown, fallback: string) =>
    (error as MutationError)?.data?.message || fallback

  const handleSubmit = async () => {
    const payload = {
      text: form.text.trim(),
      namespace: form.namespace,
    }

    if (!payload.text) {
      Toast({ type: 'error', label: 'Tag text is required.' })
      return
    }

    try {
      if (editingTag) {
        await updateTag({ id: editingTag.id, ...payload }).unwrap()
        Toast({ type: 'success', label: 'Tag updated!' })
      } else {
        await createTag(payload).unwrap()
        Toast({ type: 'success', label: 'Tag created!' })
      }

      closeModal()
    } catch (error: unknown) {
      Toast({ type: 'error', label: getErrorMessage(error, 'Failed to save tag.') })
    }
  }

  const handleDelete = (tag: TagDto) => {
    confirm('', {
      title: 'Delete Tag',
      description: 'Are you sure you want to delete this tag?',
    })
      .then(async () => {
        try {
          await deleteTag(tag.id).unwrap()
          Toast({ type: 'success', label: 'Tag deleted!' })
        } catch (error: unknown) {
          Toast({ type: 'error', label: getErrorMessage(error, 'Failed to delete tag.') })
        }
      })
      .catch(() => {})
  }

  const data = {
    items: filteredTags,
  }

  const header = [
    {
      accessor: 'text',
      Header: 'Text',
      Cell: ({ row }) => (
        <div style={{ whiteSpace: 'pre-wrap' }}>{row.original.text}</div>
      ),
    },
    {
      accessor: 'namespace',
      Header: 'Namespace',
      Cell: ({ row }) => (
        <span>{row.original.namespace === 'PATIENT' ? 'Patient' : row.original.namespace}</span>
      ),
    },
    {
      accessor: 'createdAt',
      Header: 'Created At',
      Cell: ({ row }) => <span>{moment(row.original.createdAt).format('MM/DD/YYYY h:mm a')}</span>,
    },
    {
      accessor: 'updatedAt',
      Header: 'Updated At',
      Cell: ({ row }) => <span>{moment(row.original.updatedAt).format('MM/DD/YYYY h:mm a')}</span>,
    },
    {
      accessor: 'action',
      Header: 'Action',
      Cell: ({ row }) => (
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <Button
            className="btn-sm"
            variant="primary"
            onClick={(e) => {
              e.stopPropagation()
              openEditModal(row.original)
            }}>
            Edit
          </Button>
          <Button
            className="btn-sm"
            variant="danger"
            onClick={(e) => {
              e.stopPropagation()
              handleDelete(row.original)
            }}>
            Delete
          </Button>
        </div>
      ),
    },
  ]

  if (!canManageTags) {
    return null
  }

  return (
    <>
      <DashboardContent
        title="Tags"
        icon="fas fa-tags"
        content={
          <div className="card card-primary">
            <form>
              <div className="card-header row">
                <div className="col-10 input-group mb-3">
                  <Input
                    autoFocus={true}
                    value={search}
                    label={false}
                    placeholder="Search..."
                    name="search"
                    parentDiv={null}
                    handleChange={(event) => setSearch(event.target.value)}
                  />
                  <div className="input-group-append">
                    <span className="input-group-text">
                      <i className="fas fa-search" />
                    </span>
                  </div>
                </div>
                <div className="col-2 text-right">
                  <Button type="button" className="btn btn-primary" onClick={openCreateModal}>
                    <i className="fa fa-plus-circle mr-1" />
                    New
                  </Button>
                </div>
              </div>
            </form>
            <div className="card-body align-items-center">
              <StyledTableWrapper className="card-body row">
                <DataTable
                  tableKey="tags"
                  data={data}
                  status={isLoading}
                  tableColumns={header}
                  pageSize={PAGE_SIZE}
                  currentPage={0}
                  setCurrentPage={() => {}}
                  setSortOptions={() => {}}
                  entityName="tags"
                />
              </StyledTableWrapper>
            </div>
          </div>
        }
        breadcrumb={[
          {
            label: 'Tags',
            to: `${routes.index}${routes.tags.root}${routes.tags.directory}`,
          },
        ]}
      />

      <Modal show={showModal} onHide={closeModal} centered>
        <Modal.Header>
          <Modal.Title>{editingTag ? 'Edit Tag' : 'New Tag'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-group">
            <label className="text-gray">Tag Text</label>
            <textarea
              className="form-control"
              rows={5}
              value={form.text}
              onChange={(e) => setForm((prev) => ({ ...prev, text: e.target.value }))}
              placeholder="Enter tag text"
            />
          </div>
          <div className="form-group mb-0">
            <label className="text-gray">Namespace</label>
            <select
              className="form-control"
              value={form.namespace}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  namespace: e.target.value as 'PATIENT',
                }))
              }>
              {NAMESPACE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="outline-secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={createLoading || updateLoading}
            onClick={handleSubmit}>
            {createLoading || updateLoading ? 'Saving...' : 'Submit'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default TagDirectory
